import { MarkerClusterer } from '@googlemaps/markerclusterer';
import {
	datajs,
	datajsAll,
	getMapLocations,
	getMapStyle,
	getMapIcon,
	getActiveMapIcon,
	getMapContentHTML,
	CustomClusterRenderer,
	isBreakpointMdUp,
} from '../shared/utils';
const google = window.google;

export default class MapBlock {
	static init() {
		const mapBlocks = datajsAll('map-block');

		if (!google) {
			return;
		}

		if (!mapBlocks) {
			return;
		}

		mapBlocks.forEach((mapBlock) => {
			this.setupMapBlock(mapBlock);
		});
	}

	// Setup Map Block
	static setupMapBlock(mapBlock) {
		const mapElement = datajs('map', mapBlock);

		if (!mapElement) {
			return;
		}

		const lang = mapElement.dataset.lang || 'sv';
		const initLat = parseFloat(mapElement.dataset.lat);
		const initLng = parseFloat(mapElement.dataset.lng);
		const initZoom = parseInt(mapElement.dataset.zoom, 10) || 14;
		const pageId = parseInt(mapElement.dataset.pageId);
		const postTypes = mapElement.dataset.postTypes;

		let currentMarker = null;
		let infowindow = null;
		let map = null;
		let markers = [];
		let markerClusterer = null;

		// Init the map
		map = new google.maps.Map(mapElement, {
			center: { lat: initLat, lng: initLng },
			zoom: initZoom,
			disableDefaultUI: true,
			zoomControl: true,
			zoomControlOptions: {
				position: google.maps.ControlPosition.RIGHT_TOP,
			},
			styles: getMapStyle(),
		});

		// Remove loader
		google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
			mapElement.classList.remove('has-loader');

			// Blur focus of the map element if it's focused
			if (document?.activeElement?.getAttribute('aria-label') === 'Map') {
				document.activeElement.blur();
			}
		});

		const setupMarkers = (locations) => {
			if (!locations.length) {
				return;
			}

			// Clear any existing clusterer
			if (markerClusterer) {
				markerClusterer.clearMarkers();
			}

			// Draw markers for each location
			locations.forEach((location) => {
				const id = location?.id;
				const lat = location?.lat;
				const lng = location?.lng;
				const type = location?.type;
				const icon = getMapIcon(type);
				const activeIcon = getActiveMapIcon(type);
				let hideReadMore = false;

				// if current page is this markers "home page"
				if (pageId === id) {
					hideReadMore = true;
				}

				let contentHtml = getMapContentHTML(location, hideReadMore);

				// Add marker to the map
				if (lat && lng) {
					const marker = new google.maps.Marker({
						position: new google.maps.LatLng(lat, lng),
						icon: icon,
						map: map,
						title: location?.title,
						html: contentHtml,
						cursor: 'default',
						animation: google.maps.Animation.DROP,
					});

					// We need to access the type later
					marker.type = type;

					// Add marker to global marker array for clustering
					markers.push(marker);

					// Markers with html content
					if (contentHtml) {
						marker.setCursor('pointer');

						google.maps.event.addListener(marker, 'click', () => {
							// Reset any existing active marker
							if (currentMarker) {
								currentMarker.setIcon(getMapIcon(currentMarker.type));
								currentMarker.setZIndex(0);
							}

							currentMarker = marker;
							currentMarker.setIcon(activeIcon);
							currentMarker.setZIndex(999);

							infowindow.setContent(marker.html);
							infowindow.open(map, marker);

							// Position the infowindow
							map.setCenter(marker.getPosition());
							map.panBy(0, 0);
						});
					}

					// Open an infowindow directly if current page is this markers "home page"
					if (pageId === id) {
						currentMarker = marker;
						currentMarker.setIcon(activeIcon);
						currentMarker.setZIndex(999);

						// Keep track of small/large video visibility breakpoint
						const breakpointMdUp = window.matchMedia(isBreakpointMdUp);
						if (breakpointMdUp.matches) {
							infowindow.setContent(currentMarker.html);
							infowindow.open(map, currentMarker);
							infowindow.shouldFocus = false;
						}
					}
				}
			});

			// Add a marker clusterer to manage the markers. Currently same icon for all colors.
			markerClusterer = new MarkerClusterer({
				map,
				markers,
				renderer: new CustomClusterRenderer(),
			});
		};

		// Fetch page map data
		getMapLocations({ lang, postTypes }, setupMarkers);

		// Give the infowindow some initial content
		infowindow = new google.maps.InfoWindow({
			content: 'Loading...',
			disableAutoPan: true,
		});

		// Reset icon on close
		google.maps.event.addListener(infowindow, 'closeclick', () => {
			currentMarker.setIcon(getMapIcon(currentMarker.type));
			currentMarker.setZIndex(0);
		});
	}
}
